import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Container, Row, Col, Tabs, Tab } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/monitoring.actions'
import * as fromCourseActions from 'store/actions/courses.action'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import Loader from 'components/ui/loader/Loader'
import Chat from 'components/ui/chat/Chat'
import * as fromChatActions from 'store/actions/chat.action'
import { CourseStateType } from 'enum/course-state.enum'
import * as fromAccessActions from 'store/actions/access.action'
import { coursePermissionsInitialState } from 'store/states/access.state'
import CourseResults from './components/courseResults/CourseResults'
import CourseData from './components/courseData/CourseData'
import styles from './Index.module.scss'
import UserData from './components/userData/UserData'

export const Index: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { courseId, userId } =
    useParams<Record<'courseId' | 'userId', string>>()
  const { course, loading: courseLoading } = useSelector(
    ({ courses }: IMainState) => courses
  )
  const { user, loading: userLoading } = useSelector(
    ({ monitoring }: IMainState) => monitoring
  )
  const [loading, setLoading] = useState(false)
  const { user: tokenData } = useSelector(({ user }: IMainState) => user)
  const [conversationId, setConversationId] = useState(0)
  const [tabKey, setTabKey] = useState('results')
  const { permission } = useSelector(({ user }: IMainState) => user)
  const [access, setAccess] = useState(coursePermissionsInitialState)
  const { courses: coursesPermissions } = useSelector(
    ({ access }: IMainState) => access
  )

  useEffect(() => {
    const currentCourse = coursesPermissions[Number(courseId)]
    if (currentCourse && currentCourse.id) setAccess(currentCourse)
  }, [courseId, coursesPermissions])

  useEffect(() => {
    dispatch({ type: fromAccessActions.CAN_COURSE, payload: courseId })
    dispatch({
      type: fromActions.USER_MONITORING_GET,
      payload: { courseId, userId },
    })
    dispatch({ type: fromCourseActions.GET_COURSE, payload: courseId })
  }, [courseId, dispatch, userId])

  useEffect(() => {
    if (user.conversationId >= 0) setConversationId(Number(user.conversationId))
  }, [user])

  const onSubmit = (data: any) => {
    dispatch({
      type: fromChatActions.CREATE_CHAT_MESSAGE,
      payload: { ...data, userId: userId, identifier: tokenData.identifier },
    })
  }

  useEffect(() => {
    setLoading(userLoading || courseLoading)
  }, [userLoading, courseLoading])

  return loading ? (
    <Loader overlay color />
  ) : (
    <Container fluid>
      <Row className="pt-5 ">
        <Col xs={2} lg={3} className="pt-4">
          <UserData data={user} />
        </Col>
        <Col xs={10} lg={9}>
          <Card className={`${styles.cardBorder} py-0`}>
            <Card.Body className="px-0">
              <Container className={`${styles.cardContainer}`} fluid>
                <Row className="pb-2 mx-0">
                  <Breadcrumb separator=">">
                    <BreadcrumbItem
                      href="/administration/courses"
                      text={t('administration.monitor.breadcrum.init')}
                    />
                    <BreadcrumbItem
                      href={`/administration/courses/${courseId}/monitoring`}
                      text={t('administration.monitor.breadcrum.courseMonitor')}
                    />
                    <BreadcrumbItem
                      text={t('administration.monitor.breadcrum.participant')}
                    />
                  </Breadcrumb>
                </Row>
                {(!permission.moderator ||
                  (permission.moderator && access.canAnswer)) &&
                course.state === CourseStateType.Published ? (
                  <Tabs
                    activeKey={tabKey}
                    onSelect={(key) => setTabKey(key || '')}
                    id="some-id"
                    className={`${styles.tabsMenu} mb-4`}
                  >
                    <Tab
                      eventKey="results"
                      title={t('administration.monitor.tabs.results')}
                    >
                      <CourseData courseData={course} />
                      <CourseResults />
                    </Tab>
                    {(permission.admin ||
                      (permission.moderator && access && access.canAnswer)) && (
                      <Tab
                        eventKey="messages"
                        title={t('administration.monitor.tabs.messages')}
                        className={styles.chatTab}
                      >
                        {tabKey === 'messages' && (
                          <Chat
                            fromModal
                            fromMonitoring={true}
                            onSend={onSubmit}
                            courseId={Number(courseId) || 0}
                            conversationId={conversationId}
                          />
                        )}
                      </Tab>
                    )}
                  </Tabs>
                ) : (
                  <>
                    <CourseData courseData={course} />
                    <CourseResults />
                  </>
                )}
              </Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default Index
